import React from 'react';
import { Menu, Moon, Sun, Upload, User, LogOut, ArrowLeft } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

interface NavbarProps {
  onMenuClick: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, userProfile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Get title based on current route
  const getTitle = () => {
    switch (true) {
      case location.pathname === '/dashboard':
        return 'Dashboard';
      case location.pathname.includes('/routes/'):
        return 'Route Details';
      case location.pathname === '/upload':
        return 'Upload Route';
      case location.pathname === '/settings':
        return 'Settings';
      default:
        return 'Kite';
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleTopLeftButtonClick = () => {
    const route = location.pathname; // e.g. '/dashboard', '/routes/123', '/settings'
    // find depth by counting / segments (ignoring empty ones)
    const depth = route.split('/').filter(Boolean).length;
    if ( depth == 1 ) {
      navigate('/settings');
    } else {
      // Otherwise, go back one level up (e.g. from /routes/123 to /routes)
      navigate(route.split('/').slice(0, -1).join('/') || '/dashboard');
    }
  };

  return (
    <header className="sticky bg-transparent top-0 z-10 bg-card bg-opacity-30 backdrop-blur-lg">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Top left Button */}
          {/* On mobile:
              1. shows profile picture opens settings,
              2. back button on sub pages
              3. menu items are in the bottom bar */}
          {/* On desktop: Shows burger menu that opens side menu */}
          <button
            onClick={onMenuClick}
            className="hidden shadow-md bg-white dark:bg-gray-700 sm:block p-2 rounded-lg text-text-light hover:bg-background transition-colors lg:hidden"
            aria-label="Toggle menu"
            
          >
            <Menu size={24} />
          </button>
          <button
            className="shadow-md bg-white dark:bg-gray-700 rounded-3xl p-2"
            onClick={handleTopLeftButtonClick}
          >
            {location.pathname.split('/').filter(Boolean).length > 1 ? (
              <ArrowLeft size={20} />
            ) : (
              user && user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || 'User'}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <>
                  <User size={20} />
                </>
              )
            )}
          </button>

          <Link to="/dashboard" className="flex items-center gap-0">
            <h1 className="text-xl font-bold tracking-tight hidden sm:block">
              Kite
            </h1>
          </Link>
        </div>

        <div className="flex gap-2">
          {/* <div className="bg-light text-dark p-1.5 rounded-lg">
            <img src="/icons/logo.svg" className="dark:invert" width={24} alt="Logo" />
          </div> */}
          <div className="text-lg pt-1 font-semibold md:hidden">
            {getTitle()}
          </div>
        </div>

        <div className="flex items-center gap-2">




          <button
            onClick={toggleTheme}
            className="shadow-md bg-white dark:bg-gray-700 rounded-3xl p-2 text-text-light hover:bg-background transition-colors"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* User Menu */}
          {user && (
            <div className="relative group">
              <button className="flex items-center gap-2 p-2 rounded-lg text-text-light hover:bg-background transition-colors">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-6 h-6 rounded-full"
                    onError={(e) => {
                      e.target.onerror = null; // Prevents infinite loop if fallback fails
                      e.target.src = '/default-avatar.png'; // Path to your fallback image
                    }}
                  />
                ) : (
                  <User size={20} />
                )}
                {/* Hide name on mobile */}
                <span className="hidden sm:block ml-2 text-sm font-medium">
                  {user.displayName || 'User'}
                </span>
              </button>

              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-3 border-b border-border">
                  <p className="font-medium text-sm">{user.displayName}</p>
                  <p className="text-xs text-text-light">{user.email}</p>
                  {userProfile && (
                    <p className="text-xs text-accent capitalize">{userProfile.plan} Plan</p>
                  )}
                </div>
                <div className="py-1">
                  <Link
                    to="/settings"
                    className="block px-3 py-2 text-sm hover:bg-background transition-colors"
                  >
                    Settings
                  </Link>
                  {/* <Link
                    to="/pricing"
                    className="block px-3 py-2 text-sm hover:bg-background transition-colors"
                  >
                    Upgrade Plan
                  </Link> */}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-background transition-colors flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
